<template>
  <div class="bl">
    <!-- Navbar (identico Home) -->
    <nav class="hp-nav" :class="{ 'hp-nav--scrolled': navScrolled }">
      <div class="hp-container hp-nav__inner">
        <div class="hp-nav__logo">
          <router-link to="/"><img src="/logo-booster.png" alt="VSLBooster" class="hp-nav__logo-img" /></router-link>
        </div>
        <div class="hp-nav__links hide-mobile">
          <a href="/#funcionalidades" class="hp-nav-link">Funcionalidades</a>
          <a href="/#como-funciona" class="hp-nav-link">Como funciona</a>
          <a href="/#precos" class="hp-nav-link">Precos</a>
          <a href="/#depoimentos" class="hp-nav-link">Depoimentos</a>
          <router-link to="/blog" class="hp-nav-link">Blog</router-link>
        </div>
        <div class="hp-nav__actions">
          <router-link to="/login" class="hp-nav-link" style="margin-right:4px">Entrar</router-link>
          <router-link to="/cadastro-trial" class="hp-nav-contact-btn">Fale Conosco</router-link>
        </div>
      </div>
    </nav>

    <!-- Full-width Hero -->
    <div class="bl-hero">
      <div class="bl-hero__content">
        <span class="bl-hero__badge">Blog</span>
        <h1 class="bl-hero__title">Insights sobre <span>Video Marketing</span> e Conversao</h1>
        <p class="bl-hero__sub">Estrategias, tutoriais e novidades para vender mais com Video Sales Letters</p>
        <div class="bl-hero__search-wrap">
          <feather-icon icon="SearchIcon" size="18" class="bl-hero__search-icon" />
          <input v-model="search" class="bl-hero__search" placeholder="Buscar artigos..." @keyup.enter="fetchPosts" @input="debouncedSearch" />
        </div>
      </div>
    </div>

    <!-- Main Container -->
    <div class="bl-container">
      <!-- Categories -->
      <div class="bl-cats">
        <button class="bl-cat" :class="{ 'bl-cat--active': !selectedCategory }" @click="selectedCategory = ''; fetchPosts()">
          Todos
        </button>
        <button
          v-for="cat in categories"
          :key="cat.id"
          class="bl-cat"
          :class="{ 'bl-cat--active': selectedCategory === cat.slug }"
          @click="selectedCategory = cat.slug; fetchPosts()"
        >{{ cat.name }} <span v-if="cat.post_count" class="bl-cat__count">{{ cat.post_count }}</span></button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="bl-loading">
        <div class="bl-skeleton-grid">
          <div v-for="n in 6" :key="n" class="bl-skeleton-card">
            <div class="bl-skeleton bl-skeleton--img"></div>
            <div class="bl-skeleton bl-skeleton--title"></div>
            <div class="bl-skeleton bl-skeleton--text"></div>
            <div class="bl-skeleton bl-skeleton--text bl-skeleton--short"></div>
          </div>
        </div>
      </div>

      <!-- Featured Post (first one, only page 1) -->
      <div v-if="!loading && posts.length && page === 1" class="bl-featured" @click="$router.push('/blog/' + posts[0].slug)">
        <div class="bl-featured__img" :style="{ backgroundImage: posts[0].cover_image ? 'url(' + posts[0].cover_image + ')' : 'linear-gradient(135deg, #0D1B2E, #E86A10)' }">
          <span v-if="posts[0].category_name" class="bl-featured__badge" :style="{ background: posts[0].category_color }">{{ posts[0].category_name }}</span>
        </div>
        <div class="bl-featured__body">
          <div class="bl-featured__meta">
            <span>{{ posts[0].author_name || 'VSLBooster' }}</span>
            <span>&middot;</span>
            <span>{{ formatDate(posts[0].published_at) }}</span>
          </div>
          <h2 class="bl-featured__title">{{ posts[0].title }}</h2>
          <p class="bl-featured__excerpt">{{ posts[0].excerpt }}</p>
          <span class="bl-featured__link">Ler artigo &rarr;</span>
        </div>
      </div>

      <!-- Posts Grid -->
      <div v-if="!loading && gridPosts.length" class="bl-grid">
        <article v-for="post in gridPosts" :key="post.id" class="bl-card" @click="$router.push('/blog/' + post.slug)">
          <div class="bl-card__img" :style="{ backgroundImage: post.cover_image ? 'url(' + post.cover_image + ')' : 'linear-gradient(135deg, #0D1B2E, #E86A10)' }">
            <span v-if="post.category_name" class="bl-card__badge" :style="{ background: post.category_color }">{{ post.category_name }}</span>
          </div>
          <div class="bl-card__body">
            <div class="bl-card__meta">
              <span>{{ formatDate(post.published_at) }}</span>
            </div>
            <h3 class="bl-card__title">{{ post.title }}</h3>
            <p class="bl-card__excerpt">{{ post.excerpt }}</p>
            <span class="bl-card__link">Ler mais &rarr;</span>
          </div>
        </article>
      </div>

      <!-- Empty -->
      <div v-if="!loading && !posts.length" class="bl-empty">
        <feather-icon icon="SearchIcon" size="40" />
        <h3>Nenhum artigo encontrado</h3>
        <p>Tente buscar por outro termo ou categoria</p>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="bl-pagination">
        <button class="bl-page-btn" :disabled="page <= 1" @click="page--; fetchPosts()">
          <feather-icon icon="ChevronLeftIcon" size="16" /> Anterior
        </button>
        <div class="bl-page-numbers">
          <button
            v-for="p in visiblePages" :key="p"
            class="bl-page-num" :class="{ 'bl-page-num--active': p === page }"
            @click="page = p; fetchPosts()"
          >{{ p }}</button>
        </div>
        <button class="bl-page-btn" :disabled="page >= totalPages" @click="page++; fetchPosts()">
          Proxima <feather-icon icon="ChevronRightIcon" size="16" />
        </button>
      </div>
    </div>

    <!-- Footer -->
    <footer class="bl-footer">
      <div class="bl-footer__inner">
        <div class="bl-footer__grid">
          <div>
            <img src="/logo-booster.png" alt="VSLBooster" style="height:32px;margin-bottom:12px" />
            <p class="bl-footer__desc">A plataforma completa de VSL: player, chat com IA, webinario, heatmap e analytics.</p>
          </div>
          <div>
            <div class="bl-footer__col-title">Plataforma</div>
            <router-link to="/#funcionalidades" class="bl-footer__link">Funcionalidades</router-link>
            <router-link to="/#precos" class="bl-footer__link">Precos</router-link>
            <router-link to="/cadastro-trial" class="bl-footer__link">Teste Gratis</router-link>
          </div>
          <div>
            <div class="bl-footer__col-title">Conteudo</div>
            <router-link to="/blog" class="bl-footer__link">Blog</router-link>
            <router-link to="/documentacao" class="bl-footer__link">Documentacao</router-link>
            <router-link to="/fale-conosco" class="bl-footer__link">Contato</router-link>
          </div>
        </div>
        <div class="bl-footer__bottom">
          <span>&copy; {{ new Date().getFullYear() }} VSLBooster. Todos os direitos reservados.</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'BlogList',
  data() {
    return { posts: [], categories: [], loading: true, search: '', selectedCategory: '', page: 1, totalPages: 1, navScrolled: false }
  },
  computed: {
    gridPosts() {
      return this.page === 1 && this.posts.length > 1 ? this.posts.slice(1) : this.posts
    },
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.page - 2)
      const end = Math.min(this.totalPages, start + 4)
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },
  },
  mounted() {
    this.fetchCategories()
    this.fetchPosts()
    this._onScroll = () => { this.navScrolled = window.scrollY > 20 }
    window.addEventListener('scroll', this._onScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this._onScroll)
  },
  methods: {
    debouncedSearch() {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.page = 1
        this.fetchPosts()
      }, 400)
    },
    async fetchPosts() {
      this.loading = true
      try {
        let url = `/api/dkbackend/blog/posts?page=${this.page}&limit=12`
        if (this.selectedCategory) url += `&category=${this.selectedCategory}`
        if (this.search) url += `&q=${encodeURIComponent(this.search)}`
        const resp = await fetch(url)
        const data = await resp.json()
        this.posts = data.posts || []
        this.totalPages = data.pages || 1
      } catch (e) { console.error(e) }
      this.loading = false
    },
    async fetchCategories() {
      try {
        const resp = await fetch('/api/dkbackend/blog/categories')
        this.categories = await resp.json()
      } catch {}
    },
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
    },
  },
}
</script>

<style lang="scss" scoped>
$orange: #E86A10;
$navy: #0D1B2E;

/* ── Navbar (identico Home) ──────────────────────────────────── */
.hp-container { max-width: 1120px; margin: 0 auto; padding: 0 24px; }
.hp-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; background: rgba(10,18,35,0.96); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border-bottom: 1px solid rgba(255,255,255,0.12); transition: all 0.35s ease; padding: 0 24px; }
.hp-nav--scrolled { background: rgba(10,18,35,1); box-shadow: 0 4px 24px rgba(0,0,0,0.35); border-bottom-color: rgba(255,255,255,0.15); }
.hp-nav__inner { display: flex; align-items: center; justify-content: space-between; height: 72px; }
.hp-nav__logo-img { height: 38px; width: auto; object-fit: contain; }
.hp-nav__links { display: flex; align-items: center; gap: 36px; }
.hp-nav__actions { display: flex; gap: 12px; align-items: center; }
.hp-nav-link { color: #ffffff !important; font-size: 15px; font-weight: 500; transition: all 0.2s ease; padding: 6px 0; text-decoration: none; border-bottom: 2px solid transparent; }
.hp-nav-link:hover { color: #F97316 !important; border-bottom-color: #F97316; }
.hp-nav-contact-btn { color: #ffffff; font-size: 14px; font-weight: 600; padding: 10px 22px; border-radius: 8px; border: 1.5px solid rgba(255,255,255,0.5); transition: all 0.2s ease; cursor: pointer; background: transparent; font-family: inherit; text-decoration: none; display: inline-block; }
.hp-nav-contact-btn:hover { background: rgba(255,255,255,0.15); color: #fff; border-color: rgba(255,255,255,0.8); }
.hide-mobile {}
@media (max-width: 640px) { .hide-mobile { display: none !important; } }

/* ── Footer ─────────────────────────────────────────────────── */
.bl-footer { background: #060d1a; padding: 56px 32px 36px; }
.bl-footer__inner { max-width: 1200px; margin: 0 auto; }
.bl-footer__grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 48px; margin-bottom: 36px; padding-bottom: 36px; border-bottom: 1px solid rgba(255,255,255,0.06); }
@media (max-width: 640px) { .bl-footer__grid { grid-template-columns: 1fr; gap: 28px; } }
.bl-footer__desc { color: rgba(255,255,255,0.6); font-size: 0.85rem; line-height: 1.7; max-width: 300px; }
.bl-footer__col-title { color: rgba(255,255,255,0.8); font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 14px; }
.bl-footer__link { display: block; color: rgba(255,255,255,0.55); font-size: 0.88rem; text-decoration: none; margin-bottom: 8px; &:hover { color: rgba(255,255,255,0.9); } }
.bl-footer__bottom { color: rgba(255,255,255,0.4); font-size: 0.8rem; }

.bl { background: #fff; min-height: 100vh; }

/* ── Hero ───────────────────────────────────────────────────── */
.bl-hero {
  background: linear-gradient(135deg, $navy 0%, #0a2240 60%, darken($orange, 8%) 100%);
  padding: 120px 32px 64px; text-align: center;
}
.bl-hero__content { max-width: 680px; margin: 0 auto; }
.bl-hero__badge {
  display: inline-block; padding: 6px 18px; border-radius: 20px;
  background: rgba(255,255,255,0.1); color: $orange; font-size: 13px;
  font-weight: 700; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 20px;
}
.bl-hero__title {
  font-size: 2.8rem; font-weight: 900; color: #fff;
  letter-spacing: -1px; line-height: 1.1; margin: 0 0 16px;
  span { color: $orange; }
}
@media (max-width: 768px) { .bl-hero__title { font-size: 1.8rem; } }
.bl-hero__sub { color: rgba(255,255,255,0.65); font-size: 1.1rem; margin: 0 0 32px; line-height: 1.6; }
.bl-hero__search-wrap {
  position: relative; max-width: 480px; margin: 0 auto;
}
.bl-hero__search-icon { position: absolute; left: 18px; top: 50%; transform: translateY(-50%); color: rgba(255,255,255,0.4); }
.bl-hero__search {
  width: 100%; padding: 16px 20px 16px 48px; border-radius: 12px;
  border: 1.5px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.07);
  color: #fff; font-size: 15px; outline: none; font-family: inherit;
  backdrop-filter: blur(8px);
  &::placeholder { color: rgba(255,255,255,0.4); }
  &:focus { border-color: $orange; background: rgba(255,255,255,0.1); }
}

/* ── Container ──────────────────────────────────────────────── */
.bl-container { max-width: 1200px; margin: 0 auto; padding: 32px 32px 60px; }

/* ── Categories ─────────────────────────────────────────────── */
.bl-cats { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 32px; }
.bl-cat {
  padding: 8px 20px; border-radius: 20px; border: 1.5px solid #e2e8f0;
  background: #fff; font-size: 13px; font-weight: 600; color: #475569;
  cursor: pointer; font-family: inherit; transition: all 0.18s;
  &:hover { border-color: $orange; color: $orange; }
  &--active { background: $navy; border-color: $navy; color: #fff; }
}
.bl-cat__count { font-size: 11px; opacity: 0.6; margin-left: 3px; }

/* ── Featured Post ──────────────────────────────────────────── */
.bl-featured {
  display: grid; grid-template-columns: 1.2fr 1fr; gap: 0;
  border-radius: 16px; overflow: hidden; margin-bottom: 40px;
  border: 1px solid #e2e8f0; cursor: pointer; transition: all 0.3s;
  &:hover { box-shadow: 0 16px 48px rgba(0,0,0,0.08); transform: translateY(-3px); }
}
@media (max-width: 768px) { .bl-featured { grid-template-columns: 1fr; } }
.bl-featured__img {
  min-height: 320px; background-size: cover; background-position: center;
  position: relative;
}
.bl-featured__badge {
  position: absolute; top: 20px; left: 20px; padding: 5px 14px;
  border-radius: 6px; font-size: 12px; font-weight: 700; color: #fff;
}
.bl-featured__body {
  padding: 36px; display: flex; flex-direction: column; justify-content: center;
}
.bl-featured__meta { display: flex; gap: 8px; font-size: 13px; color: #94a3b8; margin-bottom: 12px; }
.bl-featured__title { font-size: 1.6rem; font-weight: 800; color: #0f172a; margin: 0 0 12px; line-height: 1.25; }
.bl-featured__excerpt { font-size: 0.95rem; color: #64748b; line-height: 1.65; margin: 0 0 16px; }
.bl-featured__link { color: $orange; font-weight: 700; font-size: 14px; }

/* ── Grid ───────────────────────────────────────────────────── */
.bl-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
@media (max-width: 900px) { .bl-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px) { .bl-grid { grid-template-columns: 1fr; } }

.bl-card {
  background: #fff; border-radius: 14px; overflow: hidden; cursor: pointer;
  border: 1px solid #e2e8f0; transition: all 0.25s;
  &:hover { box-shadow: 0 12px 36px rgba(0,0,0,0.07); transform: translateY(-4px); }
  &:hover .bl-card__link { color: $orange; }
}
.bl-card__img {
  height: 200px; background-size: cover; background-position: center;
  position: relative;
}
.bl-card__badge {
  position: absolute; top: 14px; left: 14px; padding: 4px 12px;
  border-radius: 6px; font-size: 11px; font-weight: 700; color: #fff;
}
.bl-card__body { padding: 22px; }
.bl-card__meta { font-size: 12px; color: #94a3b8; margin-bottom: 8px; }
.bl-card__title {
  font-size: 1.05rem; font-weight: 800; color: #0f172a; margin: 0 0 8px;
  line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.bl-card__excerpt {
  font-size: 0.88rem; color: #64748b; line-height: 1.6; margin: 0 0 14px;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}
.bl-card__link { color: #94a3b8; font-weight: 600; font-size: 13px; transition: color 0.15s; }

/* ── Skeleton Loading ───────────────────────────────────────── */
.bl-skeleton-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
@media (max-width: 900px) { .bl-skeleton-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px) { .bl-skeleton-grid { grid-template-columns: 1fr; } }
.bl-skeleton-card { border-radius: 14px; overflow: hidden; border: 1px solid #e2e8f0; padding-bottom: 16px; }
.bl-skeleton {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite;
  border-radius: 6px;
}
.bl-skeleton--img { height: 200px; border-radius: 0; margin-bottom: 16px; }
.bl-skeleton--title { height: 20px; margin: 0 16px 10px; width: 70%; }
.bl-skeleton--text { height: 14px; margin: 0 16px 8px; width: 90%; }
.bl-skeleton--short { width: 50%; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ── Empty ──────────────────────────────────────────────────── */
.bl-empty { text-align: center; padding: 80px 20px; color: #94a3b8; }
.bl-empty h3 { font-size: 1.1rem; color: #475569; margin: 16px 0 6px; }
.bl-empty p { font-size: 0.9rem; }

/* ── Pagination ─────────────────────────────────────────────── */
.bl-pagination { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 40px; }
.bl-page-btn {
  display: flex; align-items: center; gap: 4px; padding: 10px 20px; border-radius: 8px;
  border: 1.5px solid #e2e8f0; background: #fff; font-size: 13px; font-weight: 600;
  color: #475569; cursor: pointer; font-family: inherit;
  &:hover:not(:disabled) { border-color: $orange; color: $orange; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
}
.bl-page-numbers { display: flex; gap: 4px; }
.bl-page-num {
  width: 36px; height: 36px; border-radius: 8px; border: 1.5px solid #e2e8f0;
  background: #fff; display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 600; color: #475569; cursor: pointer;
  &:hover { border-color: $orange; color: $orange; }
  &--active { background: $navy; border-color: $navy; color: #fff; }
}

// ── Mobile ──────────────────────────────────────────────
@media (max-width: 768px) {
  .bl-featured { grid-template-columns: 1fr; }
  .bl-featured__img { min-height: 200px; }
  .bl-featured__body { padding: 20px; }
  .bl-featured__title { font-size: 1.2rem; }
  .bl-container { padding: 24px 16px 40px; }
  .bl-cats { gap: 6px; }
  .bl-cat { padding: 6px 14px; font-size: 12px; }
  .bl-pagination { flex-wrap: wrap; gap: 8px; }
}
@media (max-width: 480px) {
  .bl-hero { padding: 100px 16px 40px; }
  .bl-hero__title { font-size: 1.5rem; }
  .bl-hero__sub { font-size: 0.9rem; margin-bottom: 24px; }
  .bl-featured__body { padding: 16px; }
  .bl-card__body { padding: 16px; }
  .bl-card__img { height: 160px; }
  .bl-page-btn { padding: 8px 12px; font-size: 12px; }
  .hp-nav__inner { padding: 0 16px; }
  .bl-footer__inner { flex-direction: column; text-align: center; }
}
</style>
